export const CLIENT_REP_PO_READ_ONLY_MESSAGE =
  'Purchase Orders are read-only for Client Reps. Only Production may make changes.';

export const ORDER_REP_RESTRICTED_PO_FIELDS = new Set([
  'supplierStatus',
  'productionRepStatus',
  'nextAction',
  'nextActionDate',
]);

export const ORDER_REP_RESTRICTED_PO_MESSAGE =
  'Order Reps cannot update PO Status, Supplier Conversation Status, Purchasing Next Action, or Purchasing Next Action Date.';
export const PROOF_MANAGEMENT_READ_ONLY_MESSAGE =
  'Proof management is read-only for Order Reps and Client Reps.';

const PROOF_MANAGEMENT_ACTIONS = new Set([
  'manage-proofs',
  'manage-binated-proofs',
  'set-all-proofs-uploaded',
]);
const SALES_PERMITTED_PO_ACTIONS = new Set([
  'generate-purchase-order',
  'email-supplier',
  'override-proof-approval',
]);
const PO_MANAGER_ROLES = new Set([
  'administrator',
  'admin',
  'super-administrator',
  'superadmin',
  'production',
  'production-rep',
  'purchasing-rep',
]);

const PROOF_MANAGEMENT_FIELDS = new Set([
  'allProofsUploaded',
]);

const normalizeRole = (role) => {
  const value = role && typeof role === 'object'
    ? role.value ?? role.name ?? role.role
    : role;
  return String(value || '').trim().toLowerCase().replace(/[_\s]+/g, '-');
};

export const getRoleList = (currentUser = {}) => {
  const roles = Array.isArray(currentUser.roles)
    ? currentUser.roles
    : currentUser.roles ? [currentUser.roles] : [];
  return [...roles, currentUser.role]
    .map(normalizeRole)
    .filter(Boolean);
};

export const isClientRepPoReadOnly = (currentUser) => {
  const roles = getRoleList(currentUser);
  return roles.includes('client-rep');
};

export const isOrderRep = (currentUser) => {
  const roles = getRoleList(currentUser);
  return roles.includes('order-rep');
};

export const isProofManagementReadOnly = (currentUser) => {
  const roles = getRoleList(currentUser);
  return roles.includes('client-rep') || roles.includes('order-rep');
};

export const canManagePoProofs = (currentUser) => !isProofManagementReadOnly(currentUser);

const hasAnyRole = (currentUser, rolesToMatch) =>
  getRoleList(currentUser).some((role) => rolesToMatch.has(role));

export const canMutatePurchaseOrder = (currentUser, options = {}) => {
  const { action = 'general', fieldName = '' } = options;
  if (PROOF_MANAGEMENT_ACTIONS.has(action)) return canManagePoProofs(currentUser);
  if (action === 'edit-po-field' && PROOF_MANAGEMENT_FIELDS.has(fieldName)) {
    return canManagePoProofs(currentUser);
  }
  if (SALES_PERMITTED_PO_ACTIONS.has(action)) {
    const roles = getRoleList(currentUser);
    return (
      roles.includes('client-rep') ||
      roles.includes('order-rep') ||
      hasAnyRole(currentUser, PO_MANAGER_ROLES)
    );
  }
  if (action === 'assign-purchasing-rep' && isClientRepPoReadOnly(currentUser)) return true;
  if (isClientRepPoReadOnly(currentUser)) return false;
  if (action === 'edit-po-field' && isOrderRep(currentUser)) {
    return !ORDER_REP_RESTRICTED_PO_FIELDS.has(fieldName);
  }
  if (action === 'delete-purchase-order') {
    return !isClientRepPoReadOnly(currentUser);
  }
  return true;
};

export const getPoMutationMessage = (currentUser, options = {}) => {
  const { action = 'general', fieldName = '' } = options;
  if (PROOF_MANAGEMENT_ACTIONS.has(action)) {
    return PROOF_MANAGEMENT_READ_ONLY_MESSAGE;
  }
  if (action === 'edit-po-field' && PROOF_MANAGEMENT_FIELDS.has(fieldName)) {
    return PROOF_MANAGEMENT_READ_ONLY_MESSAGE;
  }
  if (SALES_PERMITTED_PO_ACTIONS.has(action)) {
    return CLIENT_REP_PO_READ_ONLY_MESSAGE;
  }
  if (isClientRepPoReadOnly(currentUser)) {
    return CLIENT_REP_PO_READ_ONLY_MESSAGE;
  }
  if (action === 'edit-po-field' && isOrderRep(currentUser)) {
    if (ORDER_REP_RESTRICTED_PO_FIELDS.has(fieldName)) {
      return ORDER_REP_RESTRICTED_PO_MESSAGE;
    }
  }
  return CLIENT_REP_PO_READ_ONLY_MESSAGE;
};

export const createPoMutationGuard = (currentUser, notify) => (options = {}) => {
  if (canMutatePurchaseOrder(currentUser, options)) return false;
  notify?.(getPoMutationMessage(currentUser, options));
  return true;
};

export const createProofMutationGuard = (currentUser, notify) =>
  createPoMutationGuard(currentUser, notify);
